import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import Dashboard from './pages/Dashboard';
import Messaging from './pages/Messaging';
import IncomeTracker from './pages/IncomeTracker';
import Clients from './pages/Clients';
import Pets from './pages/Pets';
import Settings from './pages/Settings';
import ClientReply from './pages/ClientReply';
import PillCounter from './pages/PillCounter';

// Vet Worker Tools (Safe - No Medical Calculations)
import QuickReference from './pages/QuickReference';
import ShiftTasks from './pages/ShiftTasks';
import ClientNotes from './pages/ClientNotes';
import HoursTracker from './pages/HoursTracker';
import StudyNotes from './pages/StudyNotes';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pastel-pink/20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pastel-blue mx-auto mb-4"></div>
          <p className="text-brand-brown font-medium">Loading PetPages...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route wrapper (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { currentUser } = useAuth();

  if (currentUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        <Route 
          path="/signup" 
          element={
            <PublicRoute>
              <SignupPage />
            </PublicRoute>
          } 
        />
        
        {/* Public Client Reply - No Auth Required */}
        <Route 
          path="/reply/:conversationId" 
          element={<ClientReply />} 
        />

        {/* Protected Routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/messaging" 
          element={
            <ProtectedRoute>
              <Messaging />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/income" 
          element={
            <ProtectedRoute>
              <IncomeTracker />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/clients" 
          element={
            <ProtectedRoute>
              <Clients />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pets" 
          element={
            <ProtectedRoute>
              <Pets />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pill-counter" 
          element={
            <ProtectedRoute>
              <PillCounter />
            </ProtectedRoute>
          } 
        />
        
        {/* Vet Worker Tools Routes (Safe Features) */}
        <Route 
          path="/quick-reference" 
          element={
            <ProtectedRoute>
              <QuickReference />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/shift-tasks" 
          element={
            <ProtectedRoute>
              <ShiftTasks />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/client-notes" 
          element={
            <ProtectedRoute>
              <ClientNotes />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/hours-tracker" 
          element={
            <ProtectedRoute>
              <HoursTracker />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/study-notes" 
          element={
            <ProtectedRoute>
              <StudyNotes />
            </ProtectedRoute>
          } 
        />

        {/* Default Route */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
